// components/LegalRepresentatives/RepresentativeDocumentsModal.js
import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import legalRepresentativeDocumentServices from '../service/api/Documents/legalRepresentativeDocumentServices';
import { formatDate } from '../service/utils/formatters';

/**
 * Composant modal pour afficher les documents d'un représentant légal
 * @param {Object} props - Propriétés du composant
 * @param {boolean} props.isOpen - État d'ouverture du modal
 * @param {Function} props.onClose - Fonction pour fermer le modal
 * @param {Object} props.representative - Représentant légal sélectionné
 */
const RepresentativeDocumentsModal = ({ isOpen, onClose, representative }) => {
  // États pour les documents et le chargement
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Charger les documents du représentant
   */
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!isOpen || !representative?.id) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await legalRepresentativeDocumentServices.getDocumentsByLegalRepresentativeId(
          representative.id
        );

        if (result.success) {
          setDocuments(result.data || []);
        } else {
          setError(result.error || 'Erreur lors du chargement des documents');
        }
      } catch (err) {
        console.error('Error in fetchDocuments:', err);
        setError('Erreur de connexion au serveur');
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [isOpen, representative]);

  /**
   * Formater le type de document pour l'affichage
   * @param {string} type - Type de document
   * @returns {string} Type formaté
   */
  const formatDocumentType = (type) => {
    const typeMap = {
      'MANDATE_PROOF': 'Preuve de mandat',
      'IDENTITY_PROOF': 'Pièce d\'identité',
      'RESIDENCY_PROOF': 'Preuve de résidence',
      'POWER_OF_ATTORNEY': 'Procuration',
      'OTHER': 'Autre document'
    };
    
    return typeMap[type] || type;
  };

  /**
   * Obtenir l'icône selon le type de document
   * @param {string} type - Type de document
   * @returns {string} Classe CSS de l'icône
   */
  const getDocumentIcon = (type) => {
    const iconMap = {
      'MANDATE_PROOF': 'fas fa-file-contract',
      'IDENTITY_PROOF': 'fas fa-id-card',
      'RESIDENCY_PROOF': 'fas fa-home',
      'POWER_OF_ATTORNEY': 'fas fa-handshake',
      'OTHER': 'fas fa-file'
    };
    
    return iconMap[type] || 'fas fa-file';
  };

  /**
   * Obtenir la classe de badge selon le type de document
   * @param {string} type - Type de document
   * @returns {string} Classes CSS pour le badge
   */
  const getDocumentBadgeClass = (type) => {
    const badgeMap = {
      'MANDATE_PROOF': 'bg-primary bg-opacity-10 text-white border border-primary border-opacity-25',
      'IDENTITY_PROOF': 'bg-info bg-opacity-10 text-info border border-info border-opacity-25',
      'RESIDENCY_PROOF': 'bg-success bg-opacity-10 text-success border border-success border-opacity-25',
      'POWER_OF_ATTORNEY': 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25',
      'OTHER': 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25'
    };
    
    return badgeMap[type] || 'bg-secondary bg-opacity-10 text-secondary';
  };

  /**
   * Télécharger un document
   * @param {Object} document - Document à télécharger
   */
  const handleDownloadDocument = async (document) => {
    try {
      await legalRepresentativeDocumentServices.downloadDocument(
        document.url,
        document.fileName || document.name
      );
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  /**
   * Afficher un document dans un nouvel onglet
   * @param {Object} document - Document à afficher
   */
  const handleViewDocument = async (document) => {
    try {
      await legalRepresentativeDocumentServices.viewDocument(document.url);
    } catch (error) {
      console.error('Error viewing document:', error);
    }
  };

  // Si le modal n'est pas ouvert, ne rien rendre
  if (!isOpen) return null;

  // Obtenir le nom d'affichage du représentant
  const representativeName = representative?.representativeDisplayName || representative?.representativeEmail ||
                            `${representative?.firstName} ${representative?.lastName}` || 
                            'Représentant';

  return ReactDOM.createPortal(
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          
          {/* En-tête du modal */}
          <div className="modal-header bg-light">
            <div className="d-flex align-items-center">
              <i className="fas fa-file-alt me-2 text-primary"></i>
              <h5 className="modal-title text-dark mb-0">
                Documents preuve du mandataire {representativeName}
                {documents.length > 0 && (
                  <span className="badge bg-primary ms-2">
                    {documents.length}
                  </span>
                )}
              </h5>
            </div>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              disabled={loading}
            ></button>
          </div>

          {/* Corps du modal */}
          <div className="modal-body">
            
            {/* État de chargement */}
            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2 text-muted">Chargement des documents...</p>
              </div>
            )}

            {/* Message d'erreur */}
            {error && !loading && (
              <div className="alert alert-danger text-center">
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
                <div className="mt-2">
                  <button 
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => window.location.reload()}
                  >
                    <i className="fas fa-redo me-1"></i>
                    Réessayer
                  </button>
                </div>
              </div>
            )}

            {/* Aucun document */}
            {!loading && !error && documents.length === 0 && (
              <div className="text-center py-5">
                <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
                <h5 className="text-muted">Aucun document trouvé</h5>
                <p className="text-muted">
                  Aucun document n'est associé à ce représentant légal.
                </p>
              </div>
            )}

            {/* Liste des documents */}
            {!loading && !error && documents.length > 0 && (
              <div className="documents-list">
                {documents.map((document, index) => (
                  <div key={index} className="card mb-3 border">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          {/* Icône et nom du document */}
                          <div className="d-flex align-items-center mb-2">
                            <i className={`${getDocumentIcon(document.type)} fa-lg me-3 text-primary`}></i>
                            <div>
                              <h6 className="mb-0 fw-semibold">{document.name}</h6>
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {/* Badge type */}
                                <span className={`badge rounded-pill ${getDocumentBadgeClass(document.type)}`}>
                                  {formatDocumentType(document.type)}
                                </span>
                                
                                {/* Badge fichier */}
                                {document.fileName && (
                                  <span className="badge rounded-pill bg-light text-dark border">
                                    <i className="fas fa-file me-1"></i>
                                    {document.fileName.split('.').pop().toUpperCase()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Informations supplémentaires */}
                          <div className="ms-4">
                            {document.expiryTime && (
                              <div className="mb-1">
                                <small className="text-muted">
                                  <i className="fas fa-calendar-alt me-1"></i>
                                  Expire le: {formatDate(document.expiryTime)}
                                </small>
                              </div>
                            )}
                            
                            {/* {document.container && (
                              <div>
                                <small className="text-muted">
                                  <i className="fas fa-folder me-1"></i>
                                  Dossier: {document.container}
                                </small>
                              </div>
                            )} */}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="btn-group btn-group-sm ms-3">
                          {/* Bouton Voir */}
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleViewDocument(document)}
                            title="Voir le document"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          
                          {/* Bouton Télécharger */}
                          <button
                            className="btn btn-outline-success"
                            onClick={() => handleDownloadDocument(document)}
                            title="Télécharger le document"
                          >
                            <i className="fas fa-download"></i>
                          </button>
                          
                          {/* Bouton Lien direct */}
                          <a
                            href={document.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-info"
                            title="Ouvrir le lien direct"
                          >
                            <i className="fas fa-external-link-alt"></i>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Résumé */}
            {documents.length > 0 && (
              <div className="mt-4 pt-3 border-top">
                <div className="row">
                  <div className="col-md-6">
                    <small className="text-muted">
                      <i className="fas fa-info-circle me-1"></i>
                      {documents.length} document(s) disponible(s)
                    </small>
                  </div>
                  <div className="col-md-6 text-end">
                    <small className="text-muted">
                      <i className="fas fa-shield-alt me-1"></i>
                      Documents sécurisés - Accès restreint
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Pied de page */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              <i className="fas fa-times me-1"></i>
              Fermer
            </button>
            
            {documents.length > 0 && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => {
                  // Télécharger tous les documents
                  documents.forEach(doc => handleDownloadDocument(doc));
                }}
                title="Télécharger tous les documents"
              >
                <i className="fas fa-download me-1"></i>
                Tout télécharger
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RepresentativeDocumentsModal;